import {
  Account,
  getNetworkDisplayName,
  NetworkType,
} from '@mls-wallet/shared-types';
import { Loader2 } from 'lucide-react';
import { NetworkIcon } from '@/components/shared/network-icon';
import { formatUsdValue } from '@/lib/utils';
import { useAccountUsdDetails } from '@/hooks/account/useAccountUsdDetails';

/**
 * Network badge component for transaction builder
 * Matches Figma design with proper styling and network-specific icons
 */
function NetworkBadge({ networkId }: { networkId: NetworkType }) {
  return (
    <div className="bg-zinc-200 box-border flex items-center justify-center gap-[5px] px-[10px] py-[5px] rounded-[32px] shrink-0 relative">
      <NetworkIcon networkId={networkId} size="sm" />
      <p
        className="text-black font-medium shrink-0 relative"
        style={{
          fontSize: '16px',
          lineHeight: 'normal',
          fontStyle: 'normal',
        }}
      >
        {getNetworkDisplayName(networkId)}
      </p>
    </div>
  );
}

/**
 * Props for the TransactionBuilderAccountCard component
 */
interface TransactionBuilderAccountCardProps {
  account: Account;
  onClick?: () => void;
  className?: string;
}

/**
 * Account card component for the transaction builder
 * Displays account information in a card format with network badges,
 * total value, and address
 */
export function TransactionBuilderAccountCard({
  account,
  onClick,
  className = '',
}: TransactionBuilderAccountCardProps) {
  const { accountUsdDetails, isLoading } = useAccountUsdDetails(account.id);
  const totalUsdValue = isLoading
    ? null
    : accountUsdDetails
      ? Number(accountUsdDetails.totalUsdValue)
      : null;

  // Helper function to format address
  const formatAddress = (address: string) => {
    return (
      address.substring(0, 6) + '...' + address.substring(address.length - 4)
    );
  };

  return (
    <button
      className={`
        bg-neutral-50
        border border-solid border-white
        rounded-[15px]
        shadow-[0px_4px_6px_-3px_rgba(0,0,0,0.1)]
        h-[129px]
        w-full
        cursor-pointer
        hover:bg-slate-100
        transition-colors
        relative
        ${className}
      `}
      onClick={onClick}
    >
      <div className="overflow-clip relative rounded-[inherit] size-full">
        <div className="absolute left-[26px] top-[calc(50%+-0.5px)] -translate-y-1/2 right-[31px]">
          {/* First row: Account name and USD value */}
          <div className="flex items-center justify-between w-full mb-[20px]">
            <p
              className="text-black font-medium not-italic "
              style={{
                fontSize: '20px',
                lineHeight: 'normal',
                fontWeight: '500',
              }}
            >
              {account.name}
            </p>
            {isLoading ? (
              <div className="flex justify-end min-w-[128px]">
                <Loader2 className="h-6 w-6 animate-spin text-zinc-400" />
              </div>
            ) : (
              <p
                className="text-right text-zinc-600 font-normal not-italic min-w-[128px] tabular-nums"
                style={{
                  fontSize: '20px',
                  lineHeight: 'normal',
                }}
              >
                {formatUsdValue(totalUsdValue)}
              </p>
            )}
          </div>

          {/* Second row: Network badges and address */}
          <div className="flex items-center justify-between w-full">
            <div className="flex items-start gap-[15px]">
              {account.networks.map((network) => (
                <NetworkBadge key={network} networkId={network} />
              ))}
            </div>
            <p
              className="
                text-right
                font-normal
                text-zinc-600
                not-italic
              "
              style={{
                fontFamily: 'Roboto Mono, monospace',
                fontSize: '16px',
                lineHeight: 'normal',
              }}
            >
              {formatAddress(account.address)}
            </p>
          </div>
        </div>
      </div>
    </button>
  );
}
